import { useEffect, useState } from "react"
import {
  AlertTriangle, Calendar, Check, FileText, FolderPlus, ListChecks,
  Mail, Phone, Pencil, Send, UserPlus, Users, X,
} from "lucide-react"

/**
 * Bestaetigung fuer eine Aktionsliste.
 *
 * Ersetzt zwei gestapelte `window.confirm`. Das war aus drei Gruenden
 * schlecht:
 *
 * 1. Zwei gleich aussehende Popups hintereinander erzieht zum Wegklicken —
 *    genau das Gegenteil dessen, was eine doppelte Bestaetigung erreichen soll.
 * 2. Ein Systemdialog kann nicht zeigen, WAS nach aussen geht. Hier steht es
 *    farbig markiert daneben.
 * 3. `window.confirm` blockiert das Chrome DevTools Protocol — Klicks laufen
 *    dann ins Leere, was hier schon einmal Stunden gekostet hat.
 *
 * Die doppelte Bestaetigung bleibt: bei Aussenwirkung folgt auf „Weiter" eine
 * zweite Ansicht, die nur noch die nach aussen wirkenden Aktionen zeigt und
 * einen anders benannten Knopf hat. Zwei bewusste Schritte, ein Dialog.
 */

export type ConfirmLine = {
  type: string
  text: string
  risk?: string | null
  implemented?: boolean
  /** #149/#148: z. B. „Ohne Mitarbeiter …" — ein Hinweis an der Zeile, KEINE
   *  Sperre. Kommt vom Server (`preview-actions`). */
  warnung?: string | null
}

const ICONS: Record<string, typeof Mail> = {
  kontakt_anlegen: UserPlus,
  kontakt_korrigieren: Pencil,
  projekt_anlegen: FolderPlus,
  termin_buchen: Calendar,
  aufgabe_anlegen: ListChecks,
  rueckruf_notieren: Phone,
  intern_informieren: Users,
  wiedervorlage: Calendar,
  entscheidung_vorlegen: ListChecks,
  dokument_ablegen: FileText,
  antwort_senden: Send,
}

export function ActionConfirmDialog({
  open, lines, needsDouble, busy, onCancel, onConfirm,
}: {
  open: boolean
  lines: ConfirmLine[]
  needsDouble: boolean
  busy?: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const [schritt, setSchritt] = useState<1 | 2>(1)

  useEffect(() => {
    if (open) setSchritt(1)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onCancel])

  if (!open) return null

  const aussen = lines.filter((l) => l.risk === "external")
  const zweiterSchritt = needsDouble && schritt === 2
  const sichtbar = zweiterSchritt ? aussen : lines

  return (
    <div
      className="pp-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel() }}
      role="dialog"
      aria-modal="true"
      aria-label={zweiterSchritt ? "Aussenwirkung bestaetigen" : "Aktionen bestaetigen"}
    >
      <div style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 520,
        boxShadow: "0 20px 60px rgba(0,0,0,.25)", overflow: "hidden",
      }}>
        <div style={{
          padding: "14px 20px", borderBottom: "0.5px solid rgba(44,44,42,0.1)",
          display: "flex", alignItems: "center", gap: 10,
          background: zweiterSchritt ? "#fff7ed" : undefined,
        }}>
          {zweiterSchritt
            ? <AlertTriangle size={16} color="#c2410c" />
            : <Check size={16} color="#3C3489" />}
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            {zweiterSchritt ? "Das wird nach aussen sichtbar" : "Diese Aktionen werden ausgefuehrt"}
          </h3>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onCancel} aria-label="Schliessen" disabled={busy}
            style={{ background: "none", border: "none", cursor: busy ? "default" : "pointer", padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          {zweiterSchritt && (
            <div style={{ fontSize: 12.5, color: "#7c2d12", lineHeight: 1.5 }}>
              Der Kunde bekommt das zu sehen. Bitte noch einmal lesen.
            </div>
          )}

          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {sichtbar.map((l, i) => {
              const Icon = ICONS[l.type] || Mail
              const extern = l.risk === "external"
              return (
                <li key={`${l.type}-${i}`} style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  fontSize: 13, lineHeight: 1.45,
                  padding: "8px 10px", borderRadius: 8,
                  background: extern ? "#fff7ed" : "rgba(44,44,42,0.035)",
                  border: extern ? "1px solid #fed7aa" : "1px solid transparent",
                }}>
                  <Icon size={14} style={{ marginTop: 2, flexShrink: 0 }}
                    color={extern ? "#c2410c" : "#5F5E5A"} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {l.text}
                    {l.warnung && (
                      <span role="alert" style={{
                        display: "flex", alignItems: "flex-start", gap: 5, marginTop: 4,
                        fontSize: 12, color: "#92400e", background: "#fef3c7",
                        border: "1px solid #fcd34d", borderRadius: 6, padding: "4px 7px",
                      }}>
                        <AlertTriangle size={12} style={{ marginTop: 2, flexShrink: 0 }} />
                        <span>{l.warnung}</span>
                      </span>
                    )}
                  </span>
                  {extern && (
                    <span style={{
                      fontSize: 12, fontWeight: 600, color: "#c2410c",
                      background: "#ffedd5", borderRadius: 999,
                      padding: "2px 7px", whiteSpace: "nowrap", flexShrink: 0,
                    }}>
                      nach aussen
                    </span>
                  )}
                </li>
              )
            })}
          </ul>

          {!zweiterSchritt && needsDouble && (
            <div style={{ fontSize: 12, color: "#7c2d12", display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle size={12} />
              Enthaelt Aktionen mit Aussenwirkung — dafuer folgt eine zweite Bestaetigung.
            </div>
          )}
        </div>

        <div style={{
          padding: "12px 20px", borderTop: "0.5px solid rgba(44,44,42,0.1)",
          display: "flex", justifyContent: "flex-end", gap: 8,
        }}>
          <button type="button" onClick={onCancel} disabled={busy}
            style={{
              background: "#fff", border: "0.5px solid rgba(44,44,42,0.2)",
              borderRadius: 7, padding: "7px 14px", fontSize: 13,
              cursor: busy ? "default" : "pointer",
            }}>
            Abbrechen
          </button>
          <button
            type="button"
            disabled={busy || sichtbar.length === 0}
            onClick={() => {
              if (needsDouble && schritt === 1) setSchritt(2)
              else onConfirm()
            }}
            style={{
              background: zweiterSchritt ? "#c2410c" : "#3C3489",
              color: "#fff", border: 0, borderRadius: 7,
              padding: "7px 16px", fontSize: 13, fontWeight: 600,
              cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
            }}>
            {busy
              ? "Fuehre aus…"
              : needsDouble && schritt === 1
                ? "Weiter"
                : zweiterSchritt
                  ? "Ja, nach aussen ausfuehren"
                  : "Ausfuehren"}
          </button>
        </div>
      </div>
    </div>
  )
}
